/**
 * OCR image preprocessing — cleans a rendered page/photo BEFORE Tesseract OCR to
 * cut noise on scans & phone snaps and lift faded text. Pipeline:
 *   grayscale → contrast-normalize → (optional median denoise) → Otsu binarize.
 *
 * Pure Canvas 2D, ZERO dependencies. It runs on the SAME <canvas> pdfjs already
 * renders to, so there's no bundle cost and no canvas↔buffer round-trip — which
 * is exactly why we don't pull in jimp here (jimp is Node/Buffer-oriented and
 * would bloat the client bundle that deliberately lazy-loads Tesseract).
 *
 * NOTE: this standardizes/cleans the IMAGE for better OCR — it is separate from
 * the image→PDF conversion at upload (that only changes the container).
 */

export interface PreprocessOpts {
  /** luminance grayscale (default true) */
  grayscale?: boolean;
  /** percentile contrast stretch — rescues faded / low-contrast scans (default true) */
  normalize?: boolean;
  /** Otsu global threshold to pure black/white text (default true) */
  binarize?: boolean;
  /** 3×3 median filter to knock out speckle — adds ~a few hundred ms/page (default true) */
  denoise?: boolean;
}

const DEFAULTS: Required<PreprocessOpts> = {
  grayscale: true,
  normalize: true,
  binarize: true,
  denoise: true,
};

/** Value at the given cumulative percentile of an 8-bit histogram. */
export function percentile(hist: Uint32Array, total: number, pct: number): number {
  const target = total * pct;
  let cum = 0;
  for (let v = 0; v < 256; v++) {
    cum += hist[v];
    if (cum >= target) return v;
  }
  return 255;
}

/**
 * Contrast-stretch a grayscale buffer in place: map the loPct–hiPct percentile
 * band to 0–255 (rescues faded scans while ignoring outlier specks). No-op when
 * the band is degenerate (a flat image).
 */
export function normalizeGray(
  gray: Uint8ClampedArray,
  n: number,
  loPct = 0.01,
  hiPct = 0.99,
): void {
  const hist = new Uint32Array(256);
  for (let p = 0; p < n; p++) hist[gray[p]]++;
  const lo = percentile(hist, n, loPct);
  const hi = percentile(hist, n, hiPct);
  if (hi <= lo) return;
  const scale = 255 / (hi - lo);
  for (let p = 0; p < n; p++) {
    const v = (gray[p] - lo) * scale;
    gray[p] = v < 0 ? 0 : v > 255 ? 255 : v;
  }
}

/** Otsu's method — the threshold that maximizes between-class variance. */
export function otsuThreshold(gray: Uint8ClampedArray, n: number): number {
  const hist = new Uint32Array(256);
  for (let p = 0; p < n; p++) hist[gray[p]]++;
  let sum = 0;
  for (let v = 0; v < 256; v++) sum += v * hist[v];
  let sumB = 0;
  let wB = 0;
  let maxVar = -1;
  let threshold = 127;
  for (let v = 0; v < 256; v++) {
    wB += hist[v];
    if (wB === 0) continue;
    const wF = n - wB;
    if (wF === 0) break;
    sumB += v * hist[v];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > maxVar) {
      maxVar = between;
      threshold = v;
    }
  }
  return threshold;
}

/** 3×3 median filter (edge-clamped) — removes salt-and-pepper speckle. */
function medianFilter3x3(gray: Uint8ClampedArray, w: number, h: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(gray.length);
  const win = new Array<number>(9);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let k = 0;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = Math.min(h - 1, Math.max(0, y + dy));
        for (let dx = -1; dx <= 1; dx++) {
          const xx = Math.min(w - 1, Math.max(0, x + dx));
          win[k++] = gray[yy * w + xx];
        }
      }
      win.sort((a, b) => a - b);
      out[y * w + x] = win[4];
    }
  }
  return out;
}

/**
 * Clean a canvas in place for OCR. Safe to call on any 2D canvas; no-op on a
 * zero-size canvas or when a 2D context can't be obtained.
 */
export function preprocessCanvasForOcr(canvas: HTMLCanvasElement, opts: PreprocessOpts = {}): void {
  const { grayscale, normalize, binarize, denoise } = { ...DEFAULTS, ...opts };
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  const n = w * h;
  if (n === 0) return;
  // Nothing to do — leave the canvas untouched.
  if (!grayscale && !normalize && !binarize && !denoise) return;

  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;

  // 1. Collapse to a single grayscale channel (luminance) for fast processing.
  let gray = new Uint8ClampedArray(n);
  for (let i = 0, p = 0; i < d.length; i += 4, p++) {
    gray[p] = (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114) | 0;
  }

  // 2. Denoise (optional) — median filter before thresholding.
  if (denoise) gray = medianFilter3x3(gray, w, h);

  // 3. Contrast-normalize — stretch the 1st–99th percentile to 0–255 so faded
  //    ink darkens and paper whitens, ignoring outlier specks.
  if (normalize) normalizeGray(gray, n);

  // 4. Binarize — Otsu split to pure black text on white.
  if (binarize) {
    const t = otsuThreshold(gray, n);
    for (let p = 0; p < n; p++) gray[p] = gray[p] > t ? 255 : 0;
  }

  // Write the single channel back across RGB (alpha forced opaque).
  for (let i = 0, p = 0; i < d.length; i += 4, p++) {
    const v = gray[p];
    d[i] = v;
    d[i + 1] = v;
    d[i + 2] = v;
    d[i + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
}

/**
 * Rasterize an image Blob/File to a canvas, normalize its resolution into a band
 * that suits OCR (upscale small scans, downscale huge phone photos for speed +
 * less sensor noise), then preprocess it. Use for the image-direct OCR path.
 */
export async function imageToPreprocessedCanvas(
  blob: Blob,
  opts: PreprocessOpts = {},
): Promise<HTMLCanvasElement> {
  const bitmap = await createImageBitmap(blob);
  const maxDim = Math.max(bitmap.width, bitmap.height);
  let scale = 1;
  if (maxDim < 1600) scale = Math.min(3, 1600 / maxDim);
  else if (maxDim > 2600) scale = 2600 / maxDim;

  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, w, h);
    preprocessCanvasForOcr(canvas, opts);
  }
  if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close();
  return canvas;
}
